import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type UserOption = { id: string; name: string; email: string };

type Props = {
  users: UserOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
};

export function UserCombobox({ users, value, onChange, placeholder }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => users.find((u) => u.id === value) ?? null, [users, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users.slice(0, 50);
    return users
      .filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 50);
  }, [users, query]);

  useEffect(() => {
    function close(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    // 'click' rather than 'mousedown': the option's onClick handler runs in
    // the same click event, and if the document listener fires on mousedown
    // it can race ahead and tear down the dropdown before pick commits.
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  useEffect(() => {
    setActiveIdx(0);
  }, [query, open]);

  function pick(u: UserOption) {
    onChange(u.id);
    setQuery("");
    setOpen(false);
  }

  function clear() {
    onChange(null);
    setQuery("");
    inputRef.current?.focus();
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && open && filtered[activeIdx]) {
      e.preventDefault();
      pick(filtered[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const ph = placeholder ?? t("tm.userCombobox.placeholder", "— pick user —");

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div
        // No onClick on the wrapper — option clicks were bubbling here and
        // re-opening the dropdown right after pick closed it, leaving the
        // box in "open + placeholder" state. Opening now happens via the
        // input's onFocus (when the user tabs/clicks into it) and by the
        // explicit chevron button below.
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 8px",
          border: "1px solid var(--border)",
          borderRadius: 6,
          background: "var(--surface)",
          cursor: "text",
        }}
        onMouseDown={(e) => {
          // Clicking on the selected-summary span should reopen the picker.
          // Forward focus into the hidden input only when the click target is
          // not inside an option further down the tree.
          if (selected && !open) {
            e.preventDefault();
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
      >
        {selected && !open ? (
          <span style={{ flex: 1, fontSize: 14 }}>
            <b>{selected.name}</b>{" "}
            <span style={{ color: "var(--ink-3)" }}>· {selected.email}</span>
          </span>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onKeyDown={onKey}
            placeholder={selected ? `${selected.name} · ${selected.email}` : ph}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 14,
              fontFamily: "inherit",
              background: "transparent",
            }}
            aria-autocomplete="list"
            aria-expanded={open}
            role="combobox"
          />
        )}
        {selected && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
            aria-label={t("action.clear", "Clear")}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--ink-3)",
              fontSize: 16,
              cursor: "pointer",
              padding: 2,
            }}
          >
            ×
          </button>
        )}
        <span style={{ color: "var(--ink-4)", fontSize: 12 }} aria-hidden>
          ▾
        </span>
      </div>
      {open && (
        <div
          role="listbox"
          className="dd-scroll"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            maxHeight: 280,
            overflowY: "auto",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            boxShadow: "var(--shadow-2)",
            zIndex: 20,
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: 10, fontSize: 13, color: "var(--ink-3)" }}>
              {t("common.empty", "No matches.")}
            </div>
          ) : (
            filtered.map((u, i) => (
              <div
                key={u.id}
                role="option"
                aria-selected={u.id === value}
                onMouseEnter={() => setActiveIdx(i)}
                // Use mousedown (with preventDefault) instead of click so the
                // selection commits before any click/blur racing tears the
                // dropdown down. preventDefault keeps focus on the input, so
                // the picker can immediately receive a follow-up keystroke.
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  pick(u);
                }}
                style={{
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontSize: 14,
                  background:
                    i === activeIdx
                      ? "var(--tone-indigo-bg)"
                      : u.id === value
                        ? "var(--surface-2)"
                        : "transparent",
                }}
              >
                <div style={{ fontWeight: 600 }}>{u.name}</div>
                <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{u.email}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
